import { useI18n } from "./i18n";
import { INTENT_PAGES } from "./intent-page-data";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

function Arrow({ size = 18 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}

function zurichDateKey() {
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Zurich", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
  return `${values.year}-${values.month}-${values.day}`;
}

function activeSchedule(schedule, language) {
  const today = zurichDateKey();
  const future = schedule.future_schedule;
  if (future && today >= future.publish_date) {
    return {
      ...schedule,
      notice: language === "de" ? (future.notice_de || future.notice) : future.notice,
      classes: future.classes,
    };
  }
  return schedule;
}

function localizedClass(item, language) {
  if (language !== "de") return item;
  return {
    ...item,
    title: item.title_de || item.title,
    format: item.format_de || item.format,
    level: item.level_de || item.level,
  };
}

function relevantClasses(page, schedule) {
  const classes = schedule.classes || [];
  if (page.scheduleMode === "no-gi") {
    return classes.filter((item) => /no-gi/i.test(`${item.title} ${item.format}`));
  }
  if (page.scheduleMode === "trial") {
    return classes.filter((item) => /fundamental|beginner|grundlagen|anfänger/i.test(`${item.title} ${item.title_de || ""} ${item.level} ${item.level_de || ""}`));
  }
  if (page.scheduleMode === "competition") {
    return classes.filter((item) => /competition|sparring|drills/i.test(`${item.title} ${item.title_de || ""} ${item.format} ${item.level}`));
  }
  return [];
}

function SchedulePreview({ classes, language, ui, root }) {
  if (!classes.length) return null;
  const localized = classes.map((item) => localizedClass(item, language));
  const groups = DAYS.map((day) => [day, localized.filter((item) => item.day === day)]).filter(([, items]) => items.length);
  return <div className="seo-schedule-grid">
    {groups.map(([day, items]) => <article key={day}>
      <h3>{ui.days[day]}</h3>
      {items.map((item, index) => <div className="seo-class-row" key={`${day}-${item.start}-${item.title}-${index}`}>
        <time>{item.start}</time>
        <div><strong>{item.title}</strong><span>{item.format} · {item.level}</span></div>
      </div>)}
    </article>)}
    <p className="seo-schedule-link"><a className="text-link" href={`${root}#schedule`}>{language === "de" ? "Vollständigen Trainingsplan ansehen" : "See the full schedule"} <Arrow /></a></p>
  </div>;
}

function LandingHeader({ language, page, text, ui }) {
  const root = `/${language}/`;
  return <>
    <a className="skip-link" href="#main">{ui.skip}</a>
    <div className="announcement"><a href={`${root}#trial`}>{text.announcement}<Arrow size={15} /></a></div>
    <header className="header seo-header">
      <a className="logo" href={root} aria-label="Tiago de Paula BJJ home"><span className="logo__mark"><img src="/images/logo.svg" alt="" /></span><span className="logo__type"><strong>TIAGO DE PAULA</strong><small>BRAZILIAN JIU-JITSU</small></span></a>
      <nav className="nav seo-nav" aria-label="Landing page navigation">
        <a href={root}>{language === "de" ? "Startseite" : "Home"}</a>
        <a href={`${root}#schedule`}>{ui.nav.schedule}</a>
        <a href={`${root}#pricing`}>{ui.nav.pricing}</a>
        <a href={`${root}#coaches`}>{ui.nav.team}</a>
        <div className="language-switch" aria-label="Language"><a className={language === "de" ? "active" : ""} href={`/de/${page.slug}/`}>DE</a><span>/</span><a className={language === "en" ? "active" : ""} href={`/en/${page.slug}/`}>EN</a></div>
        <a className="button button--small" href={`${root}#trial`}>{ui.nav.trial} <Arrow /></a>
      </nav>
    </header>
  </>;
}

const relatedPages = [
  ["no-gi-bjj-zurich", "No-Gi BJJ"],
  ["private-bjj-lessons-zurich", "Private Lessons"],
  ["bjj-trial-zurich", "Trial Class"],
  ["competition-bjj-zurich", "Competition BJJ"],
];

export default function IntentLandingPage({ pageId }) {
  const page = INTENT_PAGES[pageId];
  const { language, content: { site, pricing, zurichSchedule, silverSchedule }, ui } = useI18n();
  if (!page) return null;
  const text = page.copy[language];
  const root = `/${language}/`;
  const dojo = site.dojos.find((item) => item.id === page.dojoId);
  const sourceSchedule = page.dojoId === "silver" ? silverSchedule : zurichSchedule;
  const schedule = activeSchedule(sourceSchedule, language);
  const classes = relevantClasses(page, schedule);
  const coaches = page.coachNames.map((name) => site.coaches.find((coach) => coach.name === name)).filter(Boolean);
  let plans = pricing.plans.filter((plan) => plan.active !== false && page.planCategories.includes(plan.category));
  if (page.priceFilter === "private-only") plans = plans.filter((plan) => /^Private Lesson/.test(plan.name));
  else plans = plans.filter((plan) => plan.featured);

  const [streetAddress, postalCity] = dojo.address.split(", ");
  const postalCode = postalCity?.split(" ")[0] || "";
  const city = page.dojoId === "silver" ? "Thalwil" : (language === "de" ? "Zürich" : "Zurich");
  const url = `https://www.tiagodepaulabjj.com/${language}/${page.slug}/`;
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      { "@type": "WebPage", "@id": `${url}#webpage`, name: text.title, description: text.intro, url },
      {
        "@type": ["SportsActivityLocation", "LocalBusiness"],
        "@id": `${url}#dojo`,
        name: `${site.brand_name} – ${dojo.name}`,
        url,
        telephone: site.phone,
        email: site.email,
        address: { "@type": "PostalAddress", streetAddress, postalCode, addressLocality: city, addressCountry: "CH" },
        sameAs: [site.instagram_profile]
      },
      { "@type": "FAQPage", mainEntity: text.faqs.map(([question, answer]) => ({ "@type": "Question", name: question, acceptedAnswer: { "@type": "Answer", text: answer } })) }
    ]
  };

  return <div id="top" className="seo-page intent-page">
    <LandingHeader language={language} page={page} text={text} ui={ui} />
    <main id="main">
      <section className="seo-hero"><div className="section-shell seo-hero__grid"><div className="seo-hero__copy"><p className="eyebrow">{text.eyebrow}</p><h1>{text.title}</h1><p className="seo-lead">{text.intro}</p><div className="button-row"><a className="button" href={`${root}#trial`}>{ui.nav.trial} <Arrow /></a><a className="text-link text-link--light" href={`${root}#schedule`}>{ui.nav.schedule} <Arrow /></a></div></div><div className="seo-hero__visual"><img src={page.heroImage} alt={text.title} /></div></div><div className="section-shell seo-facts">{text.facts.map(([value, label]) => <div key={`${value}-${label}`}><strong>{value}</strong><span>{label}</span></div>)}</div></section>

      <section className="section-pad seo-section"><div className="section-shell"><div className="section-intro"><div><p className="eyebrow">{text.whyEyebrow}</p><h2>{text.whyTitle}</h2></div><div className="section-intro__aside"><p>{text.whyCopy}</p></div></div><div className="seo-card-grid">{text.reasons.map(([title, body]) => <article className="seo-card" key={title}><h3>{title}</h3><p>{body}</p></article>)}</div></div></section>

      <section className="section-pad seo-section seo-section--dark"><div className="section-shell"><div className="section-intro section-intro--light"><div><p className="eyebrow">{text.focusEyebrow}</p><h2>{text.focusTitle}</h2></div><div className="section-intro__aside"><p>{text.focusCopy}</p></div></div>{classes.length ? <SchedulePreview classes={classes} language={language} ui={ui} root={root} /> : <div className="seo-step-grid">{text.focusItems.map(([number, title, body]) => <article key={number}><span>{number}</span><h3>{title}</h3><p>{body}</p></article>)}</div>}</div></section>

      <section className="section-pad seo-section"><div className="section-shell"><div className="section-intro"><div><p className="eyebrow">{text.programmesEyebrow}</p><h2>{text.programmesTitle}</h2></div></div><div className="seo-card-grid">{text.programmes.map(([title, body]) => <article className="seo-card seo-card--program" key={title}><h3>{title}</h3><p>{body}</p></article>)}</div></div></section>

      <section className="section-pad seo-section seo-section--paper"><div className="section-shell"><div className="section-intro"><div><p className="eyebrow">COACHING</p><h2>{language === "de" ? "Coaches, die Technik verständlich machen." : "Coaches who make technique understandable."}</h2></div><div className="section-intro__aside"><p>{language === "de" ? "Die Coaches verbinden Unterrichtserfahrung, technische Detailarbeit und eigene Praxiserfahrung auf der Matte." : "The coaching team combines teaching experience, technical detail and practical mat experience."}</p></div></div><div className="seo-coach-grid">{coaches.map((coach) => <article key={coach.name}><img src={coach.image} alt={coach.name} loading="lazy" /><div><p className="eyebrow">{coach.belt}</p><h3>{coach.name}</h3><p>{coach.roles}</p></div></article>)}</div><p className="seo-inline-link"><a className="text-link" href={`${root}#coaches`}>{ui.nav.team} <Arrow /></a></p></div></section>

      {plans.length > 0 && <section className="section-pad seo-section"><div className="section-shell"><div className="section-intro"><div><p className="eyebrow">{text.pricingEyebrow}</p><h2>{text.pricingTitle}</h2></div><div className="section-intro__aside"><p>{text.pricingCopy}</p></div></div><div className="seo-price-grid">{plans.map((plan) => <article className="seo-price" key={`${plan.name}-${plan.duration}`}><p className="eyebrow">{plan.duration}</p><h3>{plan.name}</h3><strong>CHF {Number(plan.price).toLocaleString("de-CH")}</strong><p>{plan.frequency}</p></article>)}</div><p className="seo-inline-link"><a className="text-link" href={`${root}#pricing`}>{ui.nav.pricing} <Arrow /></a></p></div></section>}

      <section className="section-pad seo-location"><div className="section-shell seo-location__grid"><div><p className="eyebrow">{text.locationEyebrow}</p><h2>{text.locationTitle}</h2><p>{text.locationCopy}</p><a className="text-link" href={dojo.map_url} target="_blank" rel="noreferrer">{language === "de" ? "Route öffnen" : "Open directions"} <Arrow /></a></div><img src={dojo.image} alt={`${site.brand_name} ${dojo.name}`} loading="lazy" /></div></section>

      <section className="section-pad seo-section seo-section--paper"><div className="section-shell"><div className="section-intro"><div><p className="eyebrow">{language === "de" ? "WEITERE THEMEN" : "EXPLORE MORE"}</p><h2>{language === "de" ? "Finde genau das Training, das du suchst." : "Find the training information you need."}</h2></div></div><div className="seo-card-grid">{relatedPages.filter(([slug]) => slug !== pageId).map(([slug, label]) => <article className="seo-card" key={slug}><h3>{label}</h3><p><a className="text-link" href={`/${language}/${slug}/`}>{language === "de" ? "Seite ansehen" : "Explore page"} <Arrow /></a></p></article>)}</div></div></section>

      <section className="section-pad faq seo-faq"><div className="section-shell"><div className="section-intro"><div><p className="eyebrow">FAQ</p><h2>{text.faqTitle}</h2></div></div><div className="faq__list">{text.faqs.map(([question, answer], index) => <details key={question} open={index === 0}><summary><span>{String(index + 1).padStart(2, "0")}</span>{question}<strong>+</strong></summary><p>{answer}</p></details>)}</div></div></section>

      <section className="seo-final"><div className="section-shell"><p className="eyebrow">TIAGO DE PAULA BJJ · {city.toUpperCase()}</p><h2>{text.finalTitle}</h2><p>{text.finalCopy}</p><a className="button" href={`${root}#trial`}>{ui.nav.trial} <Arrow /></a></div></section>
    </main>
    <script id={`intent-structured-data-${page.slug}`} type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
  </div>;
}
